import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import CustomForm from "../../../custom/Forms/CustomForm";
import SelectInput from "../../../custom/components/Select-Input/SelectInput";
import TextInput from "../../../custom/components/Text-Input/TextInput";
import TextAreaInput from "../../../custom/components/TextArea-Input/TextAreaInput";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createPageData, orderFormData } from "./SubcontractorAndCustomerFile";
import {
  dropdownData,
  getRecords,
  imageURL,
  saveRecords,
  uploadFile,
} from "../../../services/api.service";
import { toast } from "react-toastify";
import { TransactionalValidation } from "../../../custom/validations/TransactionalValidation";

import Asterisks from "../../../custom/components/Asterisks/Asterisks";
import { isAlphabet } from "../../../custom/validations/Ruls";
import CustomError from "../../../custom/components/Errors/CustomError";
import CheckboxInput from "../../../custom/components/CheckBox-Input/CheckboxInput";

const SubcontractorAndCustomerCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    payload,
    navigatePage,
    headerName,
    validationType,
    createEndpoint,
    getUpdateEndpoint,
    userType,
    subcontractorRole,
  } = createPageData();

  const initialPayload = useMemo(() => payload, []);
  const [loadingData, setLoadingData] = useState(id ? true : false);
  const [formData, setFormData] = useState(orderFormData());
  const [formErrors, setFormErrors] = useState({});
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [userListOptions] = useState(userType);
  const [selecteSubcontractorRole, setSelecteSubcontractorRole] =
    useState(null);
  const [subContractorRoleListOptions] = useState(subcontractorRole);
  const [fetchedData, setFetchedData] = useState(null);

  useEffect(() => {
    if (id) {
      getEditData(id);
    }
  }, [id]);

  useEffect(() => {
    if (fetchedData) {
      let data = fetchedData;
      setFormData((prevState) => ({
        ...prevState,
        inputData: {
          ...prevState.inputData,
          ...data,
        },
      }));
    }
  }, [fetchedData]);

  const getEditData = async (Id) => {
    try {
      let payload = {
        modelName: initialPayload.modelName,
        whereCondition: {
          id: Id,
        },
        relations: [
          {
            module: "company_documents",
            moduleAs: "documents",
          },
        ],
      };

      const responseData = await getRecords(payload, "/getRecords");

      if (responseData?.data && responseData.data.length > 0) {
        const data = responseData.data[0];
        const matchType = userType?.filter((user) => user.value == data?.type);
        const matchSubcontractorRole = subcontractorRole?.filter(
          (user) => user.value == data?.subcontractor_role
        );
        setSelectedUserType(matchType[0]);
        setSelecteSubcontractorRole(matchSubcontractorRole[0]);
        setFetchedData(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    const fieldError = TransactionalValidation(validationType, {
      [name]: value,
    });

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: fieldError[name] || null,
    }));

    setFormData((prevState) => ({
      ...prevState,
      inputData: {
        ...prevState.inputData,
        [name]: type === "checkbox" ? (checked === true ? true : false) : value,
      },
    }));
  };

  const handleUserTypeSelect = (options) => {
    const selectedValue = options ? options?.value : null;
    const fieldError = TransactionalValidation(validationType, {
      type: selectedValue,
    });

    if (selectedUserType?.value == 1) {
      setSelecteSubcontractorRole(null);
      setFormData((prevState) => ({
        ...prevState,
        inputData: {
          ...prevState.inputData,
          subcontractor_role: null,
        },
      }));
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        subcontractor_role: "",
      }));
    }

    setSelectedUserType(options);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      type: fieldError?.type || "",
    }));

    setFormData((prevState) => ({
      ...prevState,
      inputData: {
        ...prevState.inputData,
        type: selectedValue,
      },
    }));
  };

  const handleSubcontractorRoleSelect = (options) => {
    const selectedValue = options ? options?.value : null;
    const fieldError = TransactionalValidation(validationType, {
      subcontractor_role: selectedValue,
    });

    setSelecteSubcontractorRole(options);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      subcontractor_role: fieldError?.subcontractor_role || "",
    }));

    setFormData((prevState) => ({
      ...prevState,
      inputData: {
        ...prevState.inputData,
        subcontractor_role: selectedValue,
      },
    }));
  };

  // add document

  const handleAddDocuments = () => {
    const additionalErrors = {};

    const userDocuments = formData?.inputData?.documents || [];

    userDocuments.forEach((row, index) => {
      if (row.is_active) {
        ["document", "file"].forEach((field) => {
          if (!row[field]?.toString().trim()) {
            additionalErrors.documents = {
              ...additionalErrors.documents,
              [index]: {
                ...additionalErrors.documents?.[index],
                [field]: `${field
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (char) => char.toUpperCase())} is required`,
              },
            };
          }

          if (field === "document" && row[field]) {
            const alphabetError = isAlphabet(row[field], "document");
            if (alphabetError) {
              additionalErrors.documents = {
                ...additionalErrors.documents,
                [index]: {
                  ...additionalErrors.documents?.[index],
                  document: alphabetError,
                },
              };
            }
          }
        });
      }
    });

    if (Object.keys(additionalErrors).length > 0) {
      setFormErrors(additionalErrors);
      return;
    }

    const updatedDocuments = [
      ...userDocuments,
      { document: "", file: null, preview: "", is_active: true },
    ];

    setFormData({
      ...formData,
      inputData: { ...formData.inputData, documents: updatedDocuments },
    });
  };

  const handleUserDocumentsOnChange = (e, index) => {
    const { name, value } = e.target;
    const updatedDocuments = [...formData.inputData?.documents];
    updatedDocuments[index][name] = value;

    // Field-specific validation
    let fieldError = "";

    switch (name) {
      case "document":
        if (!value.trim()) {
          fieldError = "Document is required";
        } else {
          fieldError = isAlphabet(value, name.replace(/_/g, " "));
        }
        break;
      default:
        break;
    }

    // Update form errors
    setFormErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      const documentErrors = { ...updatedErrors.documents };

      if (!documentErrors[index]) {
        documentErrors[index] = {}; // Initialize the object if it's undefined
      }

      // Update or delete error message for the specific field
      if (fieldError) {
        documentErrors[index][name] = fieldError;
      } else {
        delete documentErrors[index][name]; // Delete error if no error
      }

      // Remove the index if no errors are left for that index
      if (Object.keys(documentErrors[index]).length === 0) {
        delete documentErrors[index];
      }

      // Update the errors object if any documents remain
      if (Object.keys(documentErrors).length > 0) {
        updatedErrors.documents = documentErrors;
      } else {
        delete updatedErrors.documents;
      }

      return updatedErrors;
    });

    // Update the form data with the new document value
    setFormData({
      ...formData,
      inputData: { ...formData.inputData, documents: updatedDocuments },
    });
  };

  const handleFileChange = async (e, index) => {
    const selectedFile = e.target.files[0];
    let errors = {};
    errors.file = selectedFile ? "" : "file is required.";

    if (selectedFile) {
      try {
        setFormErrors((prev) => ({
          ...prev,
          documents: {
            ...prev.documents,
            [index]: {
              ...prev.documents?.[index],
              ...errors,
            },
          },
        }));
        const readFileAsBase64 = (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
          });
        };

        const base64Data = await readFileAsBase64(selectedFile);

        const response = await uploadFile("uploads", selectedFile);

        if (response?.filePath) {
          // Create a new array to avoid mutating state directly
          const updatedDocuments = formData?.inputData?.documents?.map(
            (doc, i) =>
              i === index
                ? {
                    ...doc,
                    file: response?.fileName,
                    // preview: response?.filePath,
                    preview: base64Data,
                  } // Update the specific index
                : doc
          );

          // Update state immutably
          setFormData((prevState) => ({
            ...prevState,
            inputData: {
              ...prevState.inputData,
              documents: updatedDocuments,
            },
          }));
        }
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    }
  };

  const handleRemoveUserDocuments = (index) => {
    const updatedErrors = { ...formErrors };
    delete updatedErrors.documents?.[index];

    setFormData((prev) => {
      const updatedDetails = prev.inputData.documents.map((doc, i) => {
        if (i === index) {
          return { ...doc, is_active: false };
        }
        return doc;
      });

      return {
        ...prev,
        inputData: {
          ...prev.inputData,
          documents: updatedDetails,
        },
      };
    });

    setFormErrors(updatedErrors);
  };

  const handleImageClick = (imageSrc) => {
    window.open(imageSrc, "_blank");
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();

      if (selectedUserType?.value == 2) {
        const updatedInputData = { ...formData.inputData };
        updatedInputData.subcontractor_role = "a";
        formData.inputData = updatedInputData;
      }

      const validationErrors = TransactionalValidation(
        validationType,
        formData.inputData
      );

      if (Object.keys(validationErrors).length > 0) {
        setFormErrors(validationErrors);
        return;
      }

      // Additional validation for `documents`
      const additionalErrors = {};
      formData?.inputData?.documents?.forEach((row, index) => {
        if (row.is_active) {
          ["document", "file"].forEach((field) => {
            if (!row[field]?.toString().trim()) {
              additionalErrors.documents = {
                ...additionalErrors.documents,
                [index]: {
                  ...additionalErrors.documents?.[index],
                  [field]: `${field
                    .replace(/_/g, " ")
                    ?.replace(/\b\w/g, (char) =>
                      char?.toUpperCase()
                    )} is required`,
                },
              };
            }
            if (field === "document" && row[field]) {
              const alphabetError = isAlphabet(row[field], "document");
              if (alphabetError) {
                additionalErrors.documents = {
                  ...additionalErrors.documents,
                  [index]: {
                    ...additionalErrors.documents?.[index],
                    document: alphabetError,
                  },
                };
              }
            }
          });
        }
      });

      if (Object.keys(additionalErrors).length > 0) {
        setFormErrors(additionalErrors);
        return;
      }

      // const inputData = { ...formData.inputData };
      const inputData = {
        ...formData.inputData,
        alt_mobile_number: formData?.inputData?.alt_mobile_number || null,
      };
      if (inputData?.type === 2) {
        delete inputData.subcontractor_role;
      }

      let payload = {
        // id: id,
        modelName: initialPayload?.modelName,
        relation: initialPayload?.relation,
        inputData,
      };

      if (id) {
        payload.id = id;
      }

      setLoadingData(true);

      const responseData = id
        ? await saveRecords(payload, getUpdateEndpoint)
        : await saveRecords(payload, createEndpoint);

      if (responseData && responseData.code === 1) {
        toast.success(
          `${headerName} ${id ? "updated" : "saved"} successfully!`
        );
        navigate(navigatePage?.back);
      } else {
        toast.error(
          responseData.message || `Error while saving ${headerName}!`
        );
      }
      setLoadingData(false);
    } catch (error) {
      console.error("Error during submission:", error);
    }
  };

  return (
    <CustomForm
      title={id ? "UPDATE " + headerName : "CREATE " + headerName}
      loadingData={loadingData}
      onFormSubmit={handleSubmit}
      handleBackClick={() => navigate(navigatePage?.back)}
      id={id}
    >
      <Row className="mt-2">
        <Col md="6" className="mt-3">
          <SelectInput
            label="Type"
            value={selectedUserType}
            options={userListOptions}
            onSelect={(selected) => handleUserTypeSelect(selected)}
            placeholder="Select user type"
            labelClassName="form-label"
            isClearable={true}
            asterisk={true}
            customError={formErrors?.type}
          />
        </Col>

        {selectedUserType?.value === 1 && (
          <Col md="6" className="mt-3">
            <SelectInput
              label="Subcontractor Role"
              value={selecteSubcontractorRole}
              options={subContractorRoleListOptions}
              onSelect={(selected) => handleSubcontractorRoleSelect(selected)}
              placeholder="Select subcontractor role"
              labelClassName="form-label"
              isClearable={true}
              asterisk={true}
              customError={formErrors?.subcontractor_role}
            />
          </Col>
        )}

        <Col md="6" className="mt-3">
          <TextInput
            label="Company Name"
            name="company_name"
            value={formData?.inputData?.company_name}
            onChange={onInputChange}
            placeholder="Company name"
            parentClassName=""
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.company_name}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Contact Person"
            name="contact_person_name"
            value={formData?.inputData?.contact_person_name}
            onChange={onInputChange}
            placeholder="Contact person"
            parentClassName=""
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.contact_person_name}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Contact Person Email"
            name="contact_person_email"
            value={formData?.inputData?.contact_person_email}
            onChange={onInputChange}
            placeholder="Contact person email"
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.contact_person_email}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Mobile Number"
            name="mobile_number"
            value={formData?.inputData?.mobile_number}
            onChange={onInputChange}
            placeholder="Mobile number"
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.mobile_number}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="GST No"
            name="gst_no"
            value={formData?.inputData?.gst_no}
            onChange={onInputChange}
            placeholder="GST no"
            parentClassName=""
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.gst_no}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Alternative Mobile Number"
            name="alt_mobile_number"
            value={formData?.inputData?.alt_mobile_number}
            onChange={onInputChange}
            placeholder="Alternative mobile number"
            inputClassName="form-control"
            labelClassName="form-label"
            // customError={formErrors?.alt_mobile_number}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Pan No"
            name="pan_no"
            value={formData?.inputData?.pan_no}
            onChange={onInputChange}
            placeholder="Pan no"
            parentClassName=""
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={formErrors?.pan_no}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextAreaInput
            label="Address"
            name="address"
            value={formData?.inputData?.address}
            onChange={onInputChange}
            parentClassName=""
            inputClassName="form-control"
            labelClassName="form-label"
            placeholder="Address"
          />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col
          style={{
            marginLeft: "",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBlock: "5px",
            gap: "1rem",
          }}
        >
          <div>
            <h6 className="h-6-heading m-0">Attachments</h6>
          </div>
          <Button
            color="primary"
            className="waves-effect waves-light me-1"
            onClick={handleAddDocuments}
            style={{ background: "#006eb9" }}
          >
            Add
          </Button>
        </Col>
        <div
          className="table mt-2"
          style={{
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "500px",
            width: "100%",
            scrollBehavior: "smooth",
            scrollbarWidth: "thin",
            msOverflowStyle: "none",
            zIndex: 0,
          }}
        >
          {" "}
          <table className="table table-hover">
            <thead
              style={{
                backgroundColor: "#8AC1E8",
                position: "sticky",
                top: 0,
                backgroundColor: "#8AC1E8",
                borderBottom: "1px solid black",
                zIndex: 1,
              }}
            >
              {" "}
              <tr style={{ borderBottom: "1px solid black" }}>
                <th style={{ whiteSpace: "nowrap" }}>Sr. No</th>
                <th style={{ whiteSpace: "nowrap" }}>
                  Document <Asterisks asterisk={true} />
                </th>
                <th style={{ whiteSpace: "nowrap" }}>
                  File <Asterisks asterisk={true} />
                </th>
                <th style={{ whiteSpace: "nowrap" }}>Preview</th>
                <th style={{ whiteSpace: "nowrap" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {formData?.inputData &&
                formData?.inputData?.documents &&
                formData?.inputData?.documents
                  ?.map((item, originalIndex) => ({
                    ...item,
                    originalIndex,
                  }))
                  ?.filter((item) => item.is_active)
                  ?.map((item, filteredIndex) => (
                    <React.Fragment key={item.originalIndex}>
                      <tr
                        style={{
                          borderBottom: "1px solid #cccccc",
                          verticalAlign: "middle",
                        }}
                      >
                        <td>{filteredIndex + 1}</td>
                        <td>
                          <TextInput
                            name="document"
                            value={item.document}
                            onChange={(e) =>
                              handleUserDocumentsOnChange(e, item.originalIndex)
                            }
                            placeholder="Document"
                            inputClassName="form-control"
                            labelClassName="form-label"
                            labelStyle={{ display: "none" }}
                            asterisk={true}
                            customError={
                              formErrors?.documents?.[item.originalIndex]
                                ?.document || ""
                            }
                          />
                        </td>
                        <td>
                          <div style={{ position: "relative" }}>
                            <input
                              name="file"
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileChange(e, item.originalIndex)
                              }
                              className="form-control"
                              style={{
                                border:
                                  formErrors?.documents?.[item.originalIndex]
                                    ?.file && "1px solid red",
                                borderColor:
                                  formErrors?.documents?.[item.originalIndex]
                                    ?.file && "#d15757",
                                boxShadow:
                                  formErrors?.documents?.[item.originalIndex]
                                    ?.file &&
                                  "0px 0px 3px rgba(255, 0, 0, 0.5)",
                              }}
                            />
                            {formErrors?.documents?.[item.originalIndex]
                              ?.file && (
                              <i
                                className="fas fa-exclamation-circle"
                                style={{
                                  position: "absolute",
                                  top: "50%",
                                  right: "10px",
                                  transform: "translateY(-50%)",
                                  color: "#d15757",
                                }}
                              ></i>
                            )}
                          </div>
                          {formErrors?.documents?.[item.originalIndex]
                            ?.file && (
                            <CustomError
                              error={
                                formErrors?.documents?.[item.originalIndex]
                                  ?.file || ""
                              }
                            />
                          )}
                        </td>
                        <td>
                          {item?.preview ? (
                            <img
                              src={item?.preview}
                              alt="Preview"
                              style={{ width: "100px", height: "100px" }}
                              onError={(e) =>
                                (e.target.src = "/placeholder.png")
                              }
                            />
                          ) : item?.file ? (
                            <img
                              src={imageURL + item?.file}
                              onClick={() =>
                                handleImageClick(imageURL + item?.file)
                              }
                              alt="Preview"
                              style={{
                                width: "100px",
                                height: "100px",
                                cursor: "pointer",
                              }}
                              onError={(e) =>
                                (e.target.src = "/placeholder.png")
                              }
                            />
                          ) : (
                            <span>Preview</span>
                          )}
                        </td>

                        <td>
                          <div
                            onClick={() =>
                              handleRemoveUserDocuments(item.originalIndex)
                            }
                          >
                            <Link className="text-danger">
                              <i className="mdi mdi-trash-can font-size-20"></i>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
            </tbody>
          </table>
        </div>
      </Row>

      <Row className="mt-4">
        <Col lg="12">
          <CheckboxInput
            label="Is Active"
            name="is_active"
            value={formData.inputData.is_active}
            checked={formData.inputData.is_active}
            handleTextChange={onInputChange}
            parentClassName="form-check"
            checkBoxClassName="form-check-input"
            labelClassName="form-check-label"
            disabled={false}
            required={false}
          />
        </Col>
      </Row>
    </CustomForm>
  );
};

export default SubcontractorAndCustomerCreate;
